/**
 * VendorService
 * Service for managing vendors with view endpoints
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for vendor CRUD operations with view endpoints
 */
export class VendorService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorService.DEFAULT_SERVICE_PATH,
            basePath: VendorService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get all vendor views
     * @returns Array of vendor model views
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor views with full response
     * @returns ResponseData with array of vendor model views
     */
    getViewsRaw() {
        const url = this.buildUrl('views');
        return this.http.get(url);
    }
    /**
     * Get filtered vendor list with pagination
     * @param filter - Optional filter parameters
     * @returns Paginated vendor list response
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        if (!response.data) {
            return {
                items: [],
                totalCount: 0,
                skip: filter?.skip ?? 0,
                take: filter?.take ?? 20,
                hasMore: false,
            };
        }
        return response.data;
    }
    /**
     * Get filtered vendor list with pagination and full response
     * @param filter - Optional filter parameters
     * @returns ResponseData with paginated vendor list
     */
    listViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter);
    }
    /**
     * Get vendor view by ID
     * @param id - Vendor ID
     * @returns Vendor model view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Vendor view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get vendor view by ID with full response
     * @param id - Vendor ID
     * @returns ResponseData with vendor model view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`${id}/view`);
        return this.http.get(url);
    }
    /**
     * Get vendor registration view by ID
     * @param id - Vendor ID
     * @returns Vendor registration model view
     */
    async getRegistration(id) {
        const response = await this.getRegistrationRaw(id);
        if (!response.data) {
            throw new Error(`Vendor registration view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get vendor registration view by ID with full response
     * @param id - Vendor ID
     * @returns ResponseData with vendor registration model view
     */
    getRegistrationRaw(id) {
        const url = this.buildUrl(`${id}/registration`);
        return this.http.get(url);
    }
    /**
     * Get vendor detail view by ID
     * @param id - Vendor ID
     * @returns Vendor detail model view with all related data
     */
    async getDetail(id) {
        const response = await this.getDetailRaw(id);
        if (!response.data) {
            throw new Error(`Vendor detail view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get vendor detail view by ID with full response
     * @param id - Vendor ID
     * @returns ResponseData with vendor detail model view
     */
    getDetailRaw(id) {
        const url = this.buildUrl(`${id}/detail`);
        return this.http.get(url);
    }
    /**
     * Change vendor status with optional reason.
     * Reason is required for statuses other than Active (4) and Approved (3).
     * @param vendorId - Vendor ID
     * @param newStatusId - New status ID to set
     * @param reason - Optional reason for the status change (required for non-Active/Approved)
     * @returns Updated vendor entity
     */
    async changeStatus(vendorId, newStatusId, reason) {
        const response = await this.changeStatusRaw(vendorId, newStatusId, reason);
        if (!response.data) {
            throw new Error(`Failed to change status for vendor ${vendorId}`);
        }
        return response.data;
    }
    /**
     * Change vendor status with optional reason - returns full response
     * @param vendorId - Vendor ID
     * @param newStatusId - New status ID to set
     * @param reason - Optional reason for the status change
     * @returns ResponseData with updated vendor entity
     */
    changeStatusRaw(vendorId, newStatusId, reason) {
        const url = this.buildUrl(`${vendorId}/change-status`);
        const request = {
            vendorID: vendorId,
            newStatusID: newStatusId,
            reason: reason ?? null,
        };
        return this.http.post(url, request);
    }
    // ==================== BULK IMPORT ====================
    /**
     * Bulk import vendors from Excel data (Admin only).
     * Creates user accounts with default password (FirstName@12345) and vendor profiles.
     * @param data - Bulk import request with vendor rows and options
     * @returns Bulk import result with success/failure details
     */
    async bulkImportVendors(data) {
        const response = await this.bulkImportVendorsRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to import vendors');
        }
        return response.data;
    }
    /**
     * Bulk import vendors from Excel data - returns full response
     * @param data - Bulk import request with vendor rows and options
     * @returns ResponseData with bulk import result
     */
    bulkImportVendorsRaw(data) {
        const url = this.buildUrl('bulk-import');
        return this.http.post(url, data);
    }
}
VendorService.DEFAULT_SERVICE_PATH = 'vendors';
VendorService.BASE_PATH = 'api/vendors';
//# sourceMappingURL=VendorService.js.map